import { useState } from "react";
import { fmtDay } from "./diaryUtils";

function ReminderToggle({ initialOff, adapter }) {
  const [off, setOff] = useState(!!initialOff);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  async function flip() {
    setBusy(true);
    setErr(null);
    try {
      setOff(await adapter.setReminders(!off));
    } catch {
      setErr("We couldn't change that — please try again.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-zinc-200 bg-white p-4" data-testid="diary-reminders">
      <div className="flex-1">
        <div className="text-base font-semibold text-zinc-950">Mid-week reminder email</div>
        <div className="text-sm text-zinc-600" data-testid="diary-reminders-state">
          {off ? "Off — we won't send a reminder. Your link keeps working." : "On — we'll send one gentle reminder part-way through the week."}
        </div>
        {err && <div className="mt-1 text-sm font-semibold text-red-700" role="alert">{err}</div>}
      </div>
      <button type="button" role="switch" aria-checked={!off} aria-label="Mid-week reminder email" onClick={flip} disabled={busy}
              data-testid="diary-reminders-toggle"
              className={`relative h-11 w-20 shrink-0 rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-400 disabled:opacity-60 ${off ? "bg-zinc-300" : "bg-[var(--pr-brand,#18181b)]"}`}>
        <span className={`absolute left-0 top-1 h-9 w-9 rounded-full bg-white shadow transition-transform duration-200 ${off ? "translate-x-1" : "translate-x-10"}`} />
      </button>
    </div>
  );
}

export function DiaryIntro({ data, adapter }) {
  const t = data.template;
  const first = data.calendar[0];
  const lastDay = data.calendar[data.calendar.length - 1];
  const short = { weekday: "short", day: "numeric", month: "short" };
  return (
    <div className="space-y-5" data-testid="diary-intro">
      {data.assisted && (
        <p className="rounded-lg bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-900" data-testid="diary-assisted-banner">Practitioner-assisted completion</p>
      )}
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{t.days}-day diary · about {t.est_minutes} minutes per entry</p>
        <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-950 sm:text-4xl" data-testid="diary-title">
          Hi {data.first_name || "there"}, here&apos;s your {t.name ? t.name.toLowerCase() : "diary"}
        </h1>
      </div>
      {t.intro && <p className="text-lg leading-relaxed text-zinc-800">{t.intro}</p>}
      <div className="rounded-2xl border border-zinc-200 bg-white p-5 text-lg leading-relaxed" data-testid="diary-window">
        <strong>{fmtDay(first.date, short)} – {fmtDay(lastDay.date, short)}</strong>. Use this same link on any day. Entries save
        automatically and you can change them until your diary is sent.
      </div>
      {t.copy?.not_monitored && <p className="rounded-xl bg-sky-50 p-4 text-base leading-relaxed text-sky-950" data-testid="diary-not-monitored">{t.copy.not_monitored}</p>}
      {t.copy?.safety_notice && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-base leading-relaxed text-amber-950" data-testid="diary-safety">
          <div className="mb-1 font-semibold">When to stop and get help</div>{t.copy.safety_notice}
        </div>
      )}
      {adapter.setReminders && <ReminderToggle initialOff={data.reminders_off} adapter={adapter} />}
    </div>
  );
}
